/* eslint react-hooks/exhaustive-deps: "warn" */
import { useContext, useMemo } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import menuData from './headerMenu.data.json';

// The menu's user-visible copy lives in headerMenu.data.json and is interpolated raw by every
// renderer (MenuColumns, useHeaderMenu dropdowns, CompanyColumn, BurgerMenu). Rather than sprinkle
// t() across each of them, translate the data once here — walking the tree and passing only the
// user-facing fields (title / label / description) through t(), leaving structural fields
// (navigation, icon, continuation) untouched. Both the desktop and burger menus consume this.
const TRANSLATABLE = new Set(['title', 'label', 'description']);

const translateDeep = <T>(value: T, t: (key: string) => string): T => {
  if (Array.isArray(value)) {
    return value.map((item) => translateDeep(item, t)) as unknown as T;
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        TRANSLATABLE.has(key) && typeof val === 'string' ? t(val) : translateDeep(val, t),
      ]),
    ) as T;
  }

  return value;
};

export const useLocalizedMenuData = (): typeof menuData => {
  const { t } = useInt();
  // useInt derives t() from ContentContext's internationalization + lang; key the memo on those so
  // the translated tree is stable per render and only recomputes when the locale/dictionary changes.
  const { lang, internationalization } = useContext(ContentContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- t is derived from lang + internationalization; keying on those keeps the memo stable without t's per-render identity churn
  return useMemo(() => translateDeep(menuData, t), [lang, internationalization]);
};
